"use client";

import type { MuhuratTimeSlot } from "@/lib/api/muhurat";
import Badge from "@ishvaram/ui/Badge";
import { SLOT_COLORS, getGoogleCalendarUrl } from "./types";
import { type PlaceResult } from "@ishvaram/ui/hooks";

interface MuhuratTimelineProps {
  slots: MuhuratTimeSlot[];
  selectedDate: string;
  selectedCity: PlaceResult | null;
}

export function MuhuratTimeline({ slots, selectedDate, selectedCity }: MuhuratTimelineProps) {
  return (
    <div className="bg-white rounded-2xl border border-ish-border p-6 sm:p-8 shadow-sm">
      <h2 className="font-serif text-lg font-bold text-ish-dark mb-6">
        Hourly Muhurat (6 AM - 9 PM)
      </h2>
      <div className="space-y-2">
        {slots.map((slot, i) => {
          const colors = SLOT_COLORS[slot.classification] || SLOT_COLORS.neutral;
          return (
            <div
              key={i}
              className={`flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-xl border transition-colors ${colors.bg} ${colors.border}`}
            >
              <div className="flex items-center gap-3 shrink-0">
                {/* Time */}
                <span className={`text-sm font-bold w-28 shrink-0 ${colors.text}`}>
                  {slot.start} - {slot.end}
                </span>

                {/* Dot */}
                <span className={`w-2 h-2 rounded-full shrink-0 ${colors.dot}`} />
              </div>

              {/* Classification + Reason */}
              <div className="min-w-0 flex-1 flex flex-wrap items-center gap-2">
                <Badge
                  label={
                    slot.classification === "shubh"
                      ? "Shubh"
                      : slot.classification === "ashubh"
                        ? "Ashubh"
                        : "Neutral"
                  }
                  variant={colors.badge}
                />
                <span className="text-xs text-ish-text-muted font-medium line-clamp-1">
                  {slot.reason}
                </span>
              </div>

              {/* Google Calendar Link for Shubh slots */}
              {slot.classification === "shubh" && (
                <a
                  href={getGoogleCalendarUrl(selectedDate, slot.start, slot.end, slot.reason, {
                    timezone: selectedCity?.timezone ?? "Asia/Kolkata",
                    cityName: selectedCity?.name ?? "New Delhi",
                  })}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-white shadow-sm border ${colors.border} ${colors.text} hover:bg-white/80 transition-all shrink-0`}
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  Add to Calendar
                </a>
              )}

              {/* Panchang details */}
              <span className="text-xs text-ish-text-muted/60 shrink-0 font-medium">
                {slot.nakshatra}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
